import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';

/* Helpers */
import get from 'lodash.get';
import {
    NUMBER_OF_PRODUCT_IMPRESSIONS,
    trackEcommerceProductImpressions,
    trackEcommerceProductClick,
} from '../../utils/tracking/searchBrowse/ecommerceTracking';

class SbSharedItemTrackingProviderComponent extends React.Component {
    constructor() {
        super();

        this.fireItemImpressionTracking = this.fireItemImpressionTracking.bind(this);
        this.fireItemClickTracking = this.fireItemClickTracking.bind(this);
        this.trackedItemImpressionIndexes = [];
    }

    componentDidMount() {
        /**
         * GA requirment - first batch of impressions should fire on page load.
         */
        this.fireItemImpressionTracking(0);
    }

    componentDidUpdate(prevProps) {
        if (this.props.searchBrowse.displayUriRef !== prevProps.searchBrowse.displayUriRef) {
            /**
             * Assume the page changed and reset tracking data so that we're able to fire tracking
             * for the new set of items.
             */
            this.trackedItemImpressionIndexes = [];
            this.fireItemImpressionTracking(0);
        }
    }

    trackProductImpression(startIndex) {
        const items = get(this.props, 'searchBrowse.edges');
        const pageType = get(this.props, 'searchBrowse.pageType');

        trackEcommerceProductImpressions({
            items,
            startIndex,
            pageType,
        });
    }

    fireItemImpressionTracking(index) {
        /**
         * Group items from index 0 to 19 to group with the number `0`, from 20 to 39 to group
         * 1 and so on.
         */
        const impressionGroup = Math.floor(index / NUMBER_OF_PRODUCT_IMPRESSIONS);
        /**
         * Very important to track indexes that have already fired - `react-lazy-load` can fire
         * `contentVisible` handler multiple times for the same item.
         */
        if (!this.trackedItemImpressionIndexes.includes(impressionGroup)) {
            this.trackProductImpression(impressionGroup * NUMBER_OF_PRODUCT_IMPRESSIONS);
            this.trackedItemImpressionIndexes.push(impressionGroup);
        }
    }

    fireItemClickTracking(index) {
        const pageType = get(this.props, 'searchBrowse.pageType');
        const items = get(this.props, 'searchBrowse.edges');
        const item = get(items, `[${index}].node`);

        trackEcommerceProductClick({
            item,
            index,
            pageType,
        });
    }

    render() {
        return this.props.children({
            fireItemImpressionTracking: this.fireItemImpressionTracking,
            fireItemClickTracking: this.fireItemClickTracking,
        });
    }
}

SbSharedItemTrackingProviderComponent.propTypes = {
    children: PropTypes.func,
    searchBrowse: PropTypes.object.isRequired,
};

/*
  Fields necessary for ecommerce tracking...
  serviceId
  title
  seller { serviceId }
  categoryPath
  contemporaryTrackingString
  displayInfo(buyerId: $userId) @include(if: $isClient) {
    pricing {
        showNet
        showSale
    }
  }
  pricing {
    amount {
        convertedAmountList {
            amount
            currency
        }
    }
    salePrice {
        convertedAmountList {
            amount
            currency
        }
    }
    netPrice {
        convertedAmountList {
            amount
            currency
        }
    }
  }
*/
export const SbSharedItemTrackingProvider = createFragmentContainer(
    SbSharedItemTrackingProviderComponent,
    {
        searchBrowse: graphql`
            fragment SbSharedItemTrackingProvider_searchBrowse on SearchBrowseQueryConnection {
                pageType
                displayUriRef
                edges {
                    node {
                        serviceId
                        title
                        categoryPath
                        browseUrl
                        categoryCode
                        contemporaryTrackingString
                        seller {
                            serviceId
                        }
                        pricing {
                            amount {
                                convertedAmountList {
                                    amount
                                    currency
                                }
                            }
                            salePrice {
                                convertedAmountList {
                                    amount
                                    currency
                                }
                            }
                            netPrice {
                                convertedAmountList {
                                    amount
                                    currency
                                }
                            }
                        }
                        displayInfo(page: searchAndBrowse, isTrade: $isTrade) {
                            pricing {
                                showNet
                                showSale
                            }
                            ctas {
                                showMakeOffer
                            }
                        }
                    }
                }
            }
        `,
    }
);
